import { useEffect, useState } from "react";
import { CaretRightOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import Button from "components/Button/Button/Index"
import {request} from "lib/request"
import {GenericModal} from "components/Modal/Modal";
import Styles from './newsletter.module.scss';
import { cdn } from "core/cdn";
import { useLanguage } from "locales";
import ConsultingDoctors from "./ConsultingDoctors";
import YesNoToggle from "components/YesNoToggle/YesNoToggle";
import profileActions from "containers/Profile/services/actions";
import { AccountDeletionModal, AccountPicto } from "containers/AccountDeletion/AccountDeletionModal";

interface NewsletterStatusResponse {
    status: string;
    subscribed: boolean;
    has_valid_email: boolean;
}

interface NewsletterProps {
    onNavigateToInformations: () => void;
    active: boolean;
}

const Newsletter = ({ onNavigateToInformations, active }: NewsletterProps) => {
    const { languages } = useLanguage()
    const dispatch = useDispatch();
    const [subscribed, setSubscribed] = useState<boolean | undefined>(undefined);
    const [hasValidEmail, setHasValidEmail] = useState<boolean | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [showDoctorList, setShowDoctorList] = useState<boolean>(false);
    const [showAccountModal, setShowAccountModal] = useState<boolean>(false);

    const applyStatus = (res: NewsletterStatusResponse) => {
        setSubscribed(res.subscribed);
        setHasValidEmail(res.has_valid_email);
    }

    // The Newsletter tab pane stays mounted (antd Tabs doesn't unmount inactive panes
    // by default), so a one-time fetch on mount would go stale if the customer adds an
    // email on the Informations tab and comes back here — refetch on every activation.
    useEffect(() => {
        if (!active) return;
        request<NewsletterStatusResponse>('/crm/newsletter/status', {method: "GET"})
            .then((res) => {
                if (res && res.status === "ok") applyStatus(res);
            })
            .catch((error) => console.error(error, { route: "/crm/newsletter/status" }))
    }, [active])

    const updateSubscription = (newSubscribed: boolean) => {
        setLoading(true);
        request<NewsletterStatusResponse>('/crm/newsletter/subscription', {
            method: "POST",
            payload: { subscribed: newSubscribed },
        })
            .then((res) => {
                if (res && res.status === "ok") {
                    applyStatus(res);
                    dispatch(profileActions.changePatientSuccess(
                        newSubscribed ? languages.newsletterSubscribeSuccess : languages.newsletterUnsubscribeSuccess
                    ));
                }
            })
            .catch((error) => {
                console.error(error, { route: "/crm/newsletter/subscription" });
                dispatch(profileActions.changePatientError(languages.newsletterUpdateError));
            })
            .finally(() => setLoading(false));
    }

    return (
        <div>
            <GenericModal visible={showDoctorList} footer={undefined} bodyStyle={{ height: 600 }}>
                <div>
                    <div style={{textAlign: "right"}} onClick={() => setShowDoctorList(false)}>
                        <img src={cdn("/images/close_pop-up.svg")} alt="close_icon" style={{paddingBottom: "40px"}}/>
                    </div>
                    <ConsultingDoctors />
              </div>
            </GenericModal>
            <AccountDeletionModal visible={showAccountModal} onClose={() => setShowAccountModal(false)} />
            <GenericModal visible={showAlertModal}
                titleProps={{ doNotAnimateTitle: true }}
                title={
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <img src={cdn("/images/picto_warning.svg")} alt="Attention - icône" width={75} height={75} />
                        <span>{languages.newsletterUnsubscribeConfirmTitle}</span>
                    </div>
                }
                contentText={languages.newsletterUnsubscribeConfirmText}
                bodyStyle={{ textAlign: "center" }}
                onCancel={() => {
                    setShowAlertModal(false);
                }}
                cancelText={languages.cancel}
                cancelButtonProps={{type: "primary"}}
                acceptText={languages.newsletterUnsubscribeConfirmAccept}
                loading={loading}
                onAccept={() => {
                    updateSubscription(false);
                    setShowAlertModal(false);
                }}
            />
            <div className={Styles.Cards}>
              {hasValidEmail === false ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "1551px", gap: "30px" }}>
                  <p style={{ fontSize: "28px", color: "#211452", textAlign: "left", margin: 0 }}>
                    {languages.newsletterNoValidEmailMessage}
                  </p>
                  <Button onClick={onNavigateToInformations} style={{ width: "486px", flexShrink: 0, whiteSpace: "nowrap" }}>
                    {languages.newsletterAddEmailCta}
                  </Button>
                </div>
              ) : (
                <div className={Styles.Card}>
                  <div className={Styles.CardContent}>
                    <img src={cdn("/icons/newsletter_mail_picto.svg")} alt="" width={75} height={75} />
                    <div className={Styles.CardText}>
                      <span className={Styles.CardTitle}>{languages.subscribePlaceholder}</span>
                      <span
                        className={Styles.CardSubtitle}
                        dangerouslySetInnerHTML={{ __html: languages.newsletterHealthInfo_html }}
                      />
                    </div>
                  </div>
                  <YesNoToggle
                    value={subscribed}
                    disabled={loading || subscribed === undefined}
                    onYes={() => updateSubscription(true)}
                    onNo={() => setShowAlertModal(true)}
                  />
                </div>
              )}
              <div className={`${Styles.Card} ${Styles.Clickable}`} onClick={() => setShowDoctorList(true)}>
                <div className={Styles.CardContent}>
                  <img src={cdn("/images/consulting_doctors.svg")} alt="" width={75} height={75} />
                  <span className={Styles.CardTitle}>{languages.listOfHealthProfessionals}</span>
                </div>
                <CaretRightOutlined className={Styles.Caret} />
              </div>
              <div className={`${Styles.Card} ${Styles.Clickable}`} onClick={() => setShowAccountModal(true)}>
                <div className={Styles.CardContent}>
                  <AccountPicto />
                  <span className={Styles.CardTitle}>{languages.myAccount}</span>
                </div>
                <CaretRightOutlined className={Styles.Caret} />
              </div>
            </div>
        </div>
    );
};

export default Newsletter;
